// BlogAdmin.js - Admin moderation dashboard for blog posts
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogApi } from '../../Services/Api';
import styles from './BlogAdmin.module.css';

const BlogAdmin = ({ userData }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedPost, setSelectedPost] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Check if user is admin
  const isAdmin = userData?.is_staff || userData?.is_superuser;

  useEffect(() => {
    if (isAdmin) {
      fetchPosts();
    }
  }, [statusFilter, isAdmin]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await blogApi.getModerationQueue(statusFilter);
      setPosts(response.data.posts);
      setPendingCount(response.data.pending_count);
    } catch (error) {
      console.error('Error fetching moderation queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (postId) => {
    setActionLoading(true);
    try {
      await blogApi.moderatePost(postId, { action: 'approve' });
      setPosts(posts.filter(p => p.id !== postId));
      setPendingCount(prev => prev - 1);
      setSelectedPost(null);
    } catch (error) {
      console.error('Error approving post:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (postId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    setActionLoading(true);
    try {
      await blogApi.moderatePost(postId, { 
        action: 'reject',
        rejection_reason: rejectionReason
      });
      setPosts(posts.filter(p => p.id !== postId));
      setPendingCount(prev => prev - 1);
      setSelectedPost(null);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting post:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Access denied for non-admins
  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.accessDenied}>
          <span className={styles.lockIcon}>🔒</span>
          <h2>Admin Access Required</h2>
          <p>You need administrator privileges to access this page.</p>
          <button onClick={() => navigate('/blog')}>Back to Blog</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/blog')}>
          ← Back to Blog
        </button>
        <div className={styles.headerContent}>
          <h1>🛡️ Blog Moderation</h1>
          <p>Review and approve community posts</p>
        </div>
        {pendingCount > 0 && (
          <div className={styles.pendingBadge}>
            {pendingCount} pending
          </div>
        )}
      </div>

      {/* Status Tabs */}
      <div className={styles.statusTabs}>
        {[
          { value: 'pending', label: '⏳ Pending Review', count: pendingCount },
          { value: 'approved', label: '✅ Approved' },
          { value: 'rejected', label: '❌ Rejected' }
        ].map(tab => (
          <button
            key={tab.value}
            className={`${styles.statusTab} ${statusFilter === tab.value ? styles.active : ''}`}
            onClick={() => setStatusFilter(tab.value)}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={styles.tabCount}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Posts Queue */}
      <div className={styles.content}>
        {/* Posts List */}
        <div className={styles.postsList}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon}>
                {statusFilter === 'pending' ? '✨' : '📭'}
              </span>
              <h3>
                {statusFilter === 'pending' 
                  ? 'All caught up!' 
                  : `No ${statusFilter} posts`}
              </h3>
              <p>
                {statusFilter === 'pending'
                  ? 'There are no posts waiting for review.'
                  : `No posts have been ${statusFilter} yet.`}
              </p>
            </div>
          ) : (
            posts.map(post => (
              <div 
                key={post.id} 
                className={`${styles.postCard} ${selectedPost?.id === post.id ? styles.selected : ''}`}
                onClick={() => setSelectedPost(post)}
              >
                <div className={styles.postHeader}>
                  {post.category && (
                    <span className={styles.postCategory}>
                      {post.category.icon} {post.category.name}
                    </span>
                  )}
                  <span className={styles.postDate}>{formatDate(post.created_at)}</span>
                </div>
                <h3 className={styles.postTitle}>{post.title}</h3>
                <p className={styles.postExcerpt}>{post.excerpt}</p>
                <div className={styles.postAuthor}>
                  <span className={styles.authorAvatar}>
                    {post.author.username.charAt(0).toUpperCase()}
                  </span>
                  <span>{post.author.username}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Preview Panel */}
        {selectedPost && (
          <div className={styles.previewPanel}>
            <div className={styles.previewHeader}>
              <h2>Preview</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setSelectedPost(null)}
              >
                ✕
              </button>
            </div>
            
            <div className={styles.previewContent}>
              {selectedPost.category && (
                <span className={styles.previewCategory}>
                  {selectedPost.category.icon} {selectedPost.category.name}
                </span>
              )}
              <h3 className={styles.previewTitle}>{selectedPost.title}</h3>
              
              <div className={styles.previewMeta}>
                <span>By <strong>{selectedPost.author.username}</strong></span>
                <span>{formatDate(selectedPost.created_at)}</span>
              </div>
              
              <div className={styles.previewBody}>
                {selectedPost.content.split('\n\n').map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            {statusFilter === 'pending' && (
              <div className={styles.actions}>
                <button
                  className={styles.approveButton}
                  onClick={() => handleApprove(selectedPost.id)}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Processing...' : '✅ Approve'}
                </button>
                
                <div className={styles.rejectSection}>
                  <textarea
                    placeholder="Reason for rejection (required)..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={2}
                  />
                  <button
                    className={styles.rejectButton}
                    onClick={() => handleReject(selectedPost.id)}
                    disabled={actionLoading || !rejectionReason.trim()}
                  >
                    {actionLoading ? 'Processing...' : '❌ Reject'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className={styles.quickStats}>
        <div className={styles.stat}>
          <span className={styles.statValue}>{pendingCount}</span>
          <span className={styles.statLabel}>Pending</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{posts.length}</span>
          <span className={styles.statLabel}>In View</span>
        </div>
      </div>
    </div>
  );
};

export default BlogAdmin;
